import {useRef, useState} from "react"
import type {RegionCode} from "@casas-diamante/types"
import {getPartnersProperties} from "../api/properties"
import type {PropertyResponse as Property} from "@/types/Property"
import type {SelectedScheme} from "@/types/SearchScheme"

export interface PropertySearchFilters {
   region: RegionCode
   searchString: string
   partners: string[]
   suspended: string | null
   available: string | null
   status: string[]
   condoClass: string[]
   scheme: SelectedScheme
}

export interface PropertySearchSnapshot {
   // A cached result must retain its server total and cursor, not only the loaded cards.
   properties: Property[]
   page: number
   totalHits: number
   noMoreInm: boolean
   filters: PropertySearchFilters
}

interface SearchCache {
   read: () => PropertySearchSnapshot | null
   save: (snapshot: PropertySearchSnapshot) => void
}

const createFilterKey = (filters: PropertySearchFilters) => JSON.stringify(filters)

const mergeProperties = (
   properties: Property[],
   nextPageProperties: Property[],
   searchString: string,
) => {
   const nextProperties = properties.concat(nextPageProperties)

   if (searchString) {
      // Text searches historically displayed a global name order across loaded pages.
      nextProperties.sort((a, b) => a.name.localeCompare(b.name))
   }

   return nextProperties
}

export function usePropertySearch(cache: SearchCache) {
   const activeSearchIdRef = useRef(0)
   const activeFiltersRef = useRef<PropertySearchFilters | null>(null)
   const [inmuebles, setInmuebles] = useState<Property[]>([])
   const [loading, setLoading] = useState(false)
   const [fetchingMore, setFetchingMore] = useState(false)
   const [noMoreInm, setNoMoreInm] = useState(false)
   const [searchPage, setSearchPage] = useState(0)
   const [totalHits, setTotalHits] = useState(0)
   const [hasSearched, setHasSearched] = useState(false)

   const applySnapshot = (snapshot: PropertySearchSnapshot) => {
      // fetchMore relies on this ref after a cache or navigation restore.
      activeFiltersRef.current = snapshot.filters
      setInmuebles(snapshot.properties)
      setSearchPage(snapshot.page)
      setTotalHits(snapshot.totalHits)
      setNoMoreInm(snapshot.noMoreInm)
      setHasSearched(true)
   }

   const hydrate = (snapshot: PropertySearchSnapshot) => {
      activeSearchIdRef.current += 1
      applySnapshot(snapshot)
   }

   const reset = (properties: Property[] = []) => {
      activeSearchIdRef.current += 1
      activeFiltersRef.current = null
      setInmuebles(properties)
      setSearchPage(0)
      setTotalHits(0)
      setNoMoreInm(false)
      setHasSearched(false)
      setLoading(false)
      setFetchingMore(false)
   }

   const search = async (filters: PropertySearchFilters) => {
      const searchId = activeSearchIdRef.current + 1
      const filterKey = createFilterKey(filters)
      activeSearchIdRef.current = searchId
      activeFiltersRef.current = filters
      setLoading(true)
      setFetchingMore(false)
      setNoMoreInm(false)
      setSearchPage(0)

      const cachedSnapshot = cache.read()
      if (cachedSnapshot && createFilterKey(cachedSnapshot.filters) === filterKey) {
         // Reuse the exact search state so pagination resumes at the cached page.
         applySnapshot(cachedSnapshot)
         setLoading(false)
         return
      }

      try {
         const res = await getPartnersProperties(
            filters.region,
            filters.searchString,
            filters.partners,
            filters.suspended || "",
            filters.available || "",
            filters.status,
            1,
            filters.condoClass,
            filters.scheme,
         )

         if (searchId !== activeSearchIdRef.current) return

         const properties = res.data.data as Property[]
         const totalHits = res.data.totalCount as number
         const snapshot: PropertySearchSnapshot = {
            properties,
            page: 1,
            totalHits,
            noMoreInm: properties.length === 0 || properties.length >= totalHits,
            filters,
         }
         // totalCount is authoritative and may be larger than this page's item count.
         applySnapshot(snapshot)
         cache.save(snapshot)
      } catch (error) {
         console.error("Failed to fetch properties:", error)
      } finally {
         if (searchId === activeSearchIdRef.current) setLoading(false)
      }
   }

   const fetchMore = async () => {
      const filters = activeFiltersRef.current
      if (!filters || loading || fetchingMore || noMoreInm) return

      const searchId = activeSearchIdRef.current
      const nextPage = searchPage + 1
      setFetchingMore(true)

      try {
         const res = await getPartnersProperties(
            filters.region,
            filters.searchString,
            filters.partners,
            filters.suspended || "",
            filters.available || "",
            filters.status,
            nextPage,
            filters.condoClass,
            filters.scheme,
         )

         if (searchId !== activeSearchIdRef.current) return

         const nextPageProperties = res.data.data as Property[]
         const nextTotalHits = res.data.totalCount as number
         const nextInmuebles = mergeProperties(inmuebles, nextPageProperties, filters.searchString)
         const snapshot: PropertySearchSnapshot = {
            properties: nextInmuebles,
            page: nextPage,
            totalHits: nextTotalHits,
            noMoreInm:
               nextPageProperties.length === 0 || nextInmuebles.length >= nextTotalHits,
            filters,
         }
         applySnapshot(snapshot)
         cache.save(snapshot)
      } catch (error) {
         console.error("Failed to load more properties:", error)
      } finally {
         if (searchId === activeSearchIdRef.current) setFetchingMore(false)
      }
   }

   return {
      inmuebles,
      loading,
      fetchingMore,
      noMoreInm,
      searchPage,
      totalHits,
      hasSearched,
      search,
      fetchMore,
      hydrate,
      reset,
   }
}
